import { useAppStore } from '@/core/stores/appStore';
import { useLocalSearchParams, usePathname } from 'expo-router';
import { REGISTRATION_TYPE, useManualRegistrationStore, type RegistrationType } from '../store/registrationStore';

export type RegistrationFlow = 'uae-pass' | 'manual-emirates-id' | 'manual-passport';

/**
 * Get the current registration flow type based on registration type
 */
export function getRegistrationFlow(registrationType: RegistrationType | null): RegistrationFlow | null {
    if (!registrationType) {
        return null;
    }
    if (registrationType === REGISTRATION_TYPE.EMIRATES_ID) {
        return 'manual-emirates-id';
    }
    if (registrationType === REGISTRATION_TYPE.PASSPORT) {
        return 'manual-passport';
    }
    return null;
}

/**
 * Check if current route is part of UAE Pass registration flow
 */
export function isUaePassRoute(pathname: string): boolean {
    const uaePassRoutes = ['/uaepass', '/uae-pass-details'];
    return uaePassRoutes.some((route) => pathname.includes(route));
}

/**
 * Check if UAE Pass registration is active
 */
export function isUaePassRegistrationActive(): boolean {
    const uaePassRegistration = useAppStore.getState().uaePassRegistration;
    return !!(uaePassRegistration?.customerId && uaePassRegistration?.registrationId);
}

/**
 * Calculate registration progress based on current route and flow type
 * @param pathname - Current route pathname (may include query params)
 * @param flow - Registration flow type ('uae-pass', 'manual-emirates-id', 'manual-passport')
 * @param registrationStatus - Optional registration status ('approved', 'rejected', 'pending', etc.)
 * @returns Progress value between 0 and 1
 */
export function calculateRegistrationProgress(
    pathname: string | undefined | null,
    flow: RegistrationFlow | null,
    registrationStatus?: string | null
): number {
    // If registration is approved, show 100% progress
    if (registrationStatus === 'approved') {
        return 1.0;
    }

    // If pathname is not provided, return default progress
    if (!pathname) {
        return 0.1;
    }

    // If no flow is determined, try to infer from route
    if (!flow) {
        if (isUaePassRoute(pathname)) {
            flow = 'uae-pass';
        } else {
            // Default to manual-passport if we can't determine
            // This will be overridden by actual flow detection
            return 0.1;
        }
    }

    // Normalize pathname for comparison - ensure it's always a string
    const normalizedPath = (pathname.toLowerCase().split('?')[0] || '').trim();
    
    // Check query params for document viewer type (for UAE users: passport-only view)
    const hasPassportOnlyParam = pathname.toLowerCase().includes('registrationtype=passport');

    switch (flow) {
        case 'uae-pass': {
            // UAE Pass Register Flow
            if (normalizedPath.includes('/uaepass')) {
                return 0.1; // Step 1: UAE Pass Auth
            }
            if (normalizedPath.includes('/uae-pass-details')) {
                return 0.2; // Step 2: UAE Pass Details
            }
            if (normalizedPath.includes('/phone-otp')) {
                return 0.4; // Step 3: Phone OTP
            }
            if (normalizedPath.includes('/email-otp')) {
                return 0.6; // Step 4: Email OTP
            }
            if (normalizedPath.includes('/registration-success')) {
                return 0.8; // Step 5: Registration Success (waiting for approval)
            }
            return 0.1; // Default to start
        }

        case 'manual-emirates-id': {
            // Manual UAE Users Register (Emirates ID) Flow
            if (normalizedPath.includes('/registration-form')) {
                return 0.1; // Step 1: Registration Form
            }
            if (normalizedPath.includes('/document-upload') || normalizedPath.includes('/document-scanner')) {
                // For UAE users: document viewer is opened twice
                // First: EID capture (default, no passport param)
                // Second: Passport capture (has registrationType=passport param)
                if (hasPassportOnlyParam) {
                    return 0.3; // Step 3: Document Capture (Passport)
                }
                return 0.2; // Step 2: Document Capture (EID)
            }
            if (normalizedPath.includes('/phone-otp')) {
                return 0.5; // Step 4: Phone OTP
            }
            if (normalizedPath.includes('/email-otp')) {
                return 0.7; // Step 5: Email OTP
            }
            if (normalizedPath.includes('/document-review')) {
                return 0.9; // Step 6: Document Review
            }
            if (normalizedPath.includes('/registration-success')) {
                return 0.95; // Step 7: Registration Success (waiting for approval)
            }
            return 0.1; // Default to start
        }

        case 'manual-passport': {
            // Manual International Register (Passport) Flow
            if (normalizedPath.includes('/registration-form')) {
                return 0.1; // Step 1: Registration Form
            }
            if (normalizedPath.includes('/document-upload') || normalizedPath.includes('/document-scanner')) {
                return 0.3; // Step 2: Document Capture
            }
            if (normalizedPath.includes('/phone-otp')) {
                return 0.5; // Step 3: Phone OTP
            }
            if (normalizedPath.includes('/email-otp')) {
                return 0.7; // Step 4: Email OTP
            }
            if (normalizedPath.includes('/document-review')) {
                return 0.9; // Step 5: Document Review
            }
            if (normalizedPath.includes('/registration-success')) {
                return 0.95; // Step 6: Registration Success (waiting for approval)
            }
            return 0.1; // Default to start
        }

        default:
            return 0.1; // Default to start
    }
}

/**
 * Hook to get current registration progress
 * Automatically detects flow type and calculates progress based on current route
 */
export function useRegistrationProgress(registrationStatus?: string | null): number {
    const pathname = usePathname();
    const searchParams = useLocalSearchParams();
    const registrationType = useManualRegistrationStore((state) => state.registrationType);
    const uaePassRegistration = useAppStore((state) => state.uaePassRegistration);
    
    // Construct full pathname with query params for progress calculation
    // This is needed to detect passport-only document viewer for UAE users
    const queryString = Object.entries(searchParams)
        .filter(([_, value]) => value !== undefined && value !== null)
        .map(([key, value]) => `${key}=${value}`)
        .join('&');
    const fullPathname = queryString ? `${pathname}?${queryString}` : pathname;
    
    // Determine flow type
    let flow: RegistrationFlow | null = null;
    
    // Check if UAE Pass registration is active
    if (uaePassRegistration?.customerId && uaePassRegistration?.registrationId) {
        flow = 'uae-pass';
    } else if (registrationType) {
        // Manual registration flow
        flow = getRegistrationFlow(registrationType);
    } else if (isUaePassRoute(pathname)) {
        // Fallback: check route for UAE Pass
        flow = 'uae-pass';
    }
    
    return calculateRegistrationProgress(fullPathname, flow, registrationStatus);
}

